import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatPrice } from "../lib/api";
import { JOURNAL_POSTS } from "../data/journal";

const Journal = () => {
  const [byName, setByName] = useState({});

  useEffect(() => {
    api
      .get("/products")
      .then((r) => {
        const map = {};
        (r.data || []).forEach((p) => (map[p.name.toLowerCase()] = p));
        setByName(map);
      })
      .catch(() => {});
  }, []);

  const productFor = (post) => (post.product ? byName[post.product.toLowerCase()] : null);
  const [lead, ...rest] = JOURNAL_POSTS;
  const leadProduct = productFor(lead) || Object.values(byName).find((p) => p.is_best_seller);

  return (
    <div data-testid="journal-page" className="min-h-screen">
      <Header />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16">
        <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-6 font-bold">
          The journal · Letters from the lab
        </p>
        <h1
          data-testid="journal-title"
          className="font-serif-display font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight gold-shimmer"
        >
          Notes on <span className="italic">scent</span>.
        </h1>
        <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#B0A496]">
          Stories from the Ambdrop atelier — how we compose, why we decant by hand, and the
          best-selling flacons our customers keep coming back for.
        </p>
      </section>

      {/* Lead story */}
      <section className="border-y border-[#2D2622]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <Link
            to={`/journal/${lead.slug}`}
            data-testid={`journal-lead-image`}
            className="md:col-span-6 block aspect-[4/5] md:aspect-[5/6] overflow-hidden bg-[#1F1A17] image-zoom-parent"
          >
            {leadProduct?.image_url && (
              <img src={leadProduct.image_url} alt={leadProduct.name} className="image-zoom w-full h-full object-cover" />
            )}
          </Link>
          <div className="md:col-span-6">
            <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-5 font-bold">
              {lead.eyebrow} · {lead.date}
            </p>
            <h2 className="font-serif-display font-light text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight text-[#F4EAD9]">
              {lead.title}
            </h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-[#B0A496]">{lead.excerpt}</p>
            <Link
              data-testid={`journal-read-${lead.slug}`}
              to={`/journal/${lead.slug}`}
              className="mt-10 inline-flex items-center gap-2 text-[11px] tracking-overline uppercase text-[#D4AF37] editorial-link font-bold"
            >
              Read the letter <ArrowUpRight size={14} strokeWidth={1.2} />
            </Link>
          </div>
        </div>
      </section>

      {/* Remaining stories */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {rest.map((post, idx) => {
            const prod = productFor(post);
            return (
              <Link
                key={post.slug}
                data-testid={`journal-card-${post.slug}`}
                to={`/journal/${post.slug}`}
                className="group block image-zoom-parent"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#1F1A17]">
                  {prod?.image_url && (
                    <img src={prod.image_url} alt={prod.name} loading="lazy" className="image-zoom w-full h-full object-cover" />
                  )}
                  {!prod && (
                    <div className="absolute inset-0 flex items-end p-6">
                      <p className="font-serif-display text-5xl text-[#D4AF37]/30 leading-none">Ambdrop</p>
                    </div>
                  )}
                  <span className="absolute top-4 left-4 bg-[#15110F]/85 backdrop-blur border border-[#D4AF37]/60 text-[#D4AF37] px-3 py-1 text-[10px] tracking-overline uppercase font-bold">
                    {post.eyebrow}
                  </span>
                </div>
                <p className="mt-5 text-[11px] tracking-overline uppercase text-[#B0A496] font-bold">{post.date}</p>
                <h3 className="mt-2 font-serif-display text-2xl md:text-3xl leading-tight text-[#F4EAD9] group-hover:text-[#D4AF37] transition-colors">
                  {post.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#B0A496]">{post.excerpt}</p>
                {prod && (
                  <p className="mt-3 text-sm text-[#D4AF37] tabular-nums">
                    {prod.name} · {formatPrice(prod.price_inr)}
                  </p>
                )}
              </Link>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Journal;
